// WHAT: Component for managing user birthdate with secure storage
// WHY:  Provides interface for setting and viewing birthdate (value hidden for privacy)
// DOMAIN: Settings → Personal Information
// DEPENDENCIES: core.invoke for backend commands
// RISKS:
//   - NOTE: Birthdate value is not displayed for security - may confuse users
//   - NOTE: Date validation may not handle all edge cases
//   - NOTE: Error handling may not provide user-friendly messages

import { core } from "@tauri-apps/api";
import { Calendar, CheckCircle, AlertCircle, Loader2, User, Edit3, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Card, Button, Label, StatusIndicator, Stack, Heading, DatePicker } from "@/components/ui";

/**
 * WHAT:
 *   Component that provides interface for managing user birthdate with secure
 *   storage and privacy protection (value not displayed).
 *
 * WHY:
 *   Enables users to set their birthdate for age-related features while
 *   protecting privacy by not displaying the actual value.
 *
 * BEHAVIOR:
 *   - Loads birthdate configuration status on mount
 *   - Shows configured/unconfigured status
 *   - Allows editing birthdate via date picker
 *   - Saves birthdate to secure storage
 *   - Hides actual birthdate value for privacy
 *
 * RISKS:
 *   - NOTE: Birthdate value is not displayed for security - may confuse users
 *   - NOTE: Date validation may not handle all edge cases
 *   - NOTE: Error handling may not provide user-friendly messages
 *
 * @returns {JSX.Element} Birthdate field component with configuration interface
 *
 */
export const BirthdateField = () => {
  const [birthdate, setBirthdate] = useState<Date | null>(null);
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    const loadBirthdate = async () => {
      try {
        const result = await core.invoke<null | string>("get_birthdate");
        if (result) {
          setIsConfigured(true);
          // NOTE: Don't set the actual birthdate value for security
        } else {
          setIsConfigured(false);
        }
      } catch (error_) {
        setError((error_ as Error).message);
        setIsConfigured(false);
      } finally {
        setLoading(false);
      }
    };

    void loadBirthdate();
  }, []);

  const handleSave = async () => {
    if (!birthdate) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Convert Date to ISO string for backend
      const dateString = birthdate.toISOString().split("T")[0];
      await core.invoke("save_birthdate", { date: dateString });
      setSuccess(true);
      setIsConfigured(true);
      setIsEditing(false);

      // Clear the input field after successful save
      setBirthdate(null);
    } catch (error_) {
      setError((error_ as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setBirthdate(null);
    setError(null);
    setSuccess(false);
  };

  const renderConfigurationForm = () => {
    if (isConfigured && !isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" gap={3}>
            <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
              <Calendar className="w-5 h-5 text-tracq-bright-blue" />
            </div>
            <Heading className="text-lg font-medium text-white" level={3}>
              {isConfigured ? "Update Your Birthdate" : "Set Your Birthdate"}
            </Heading>
          </Stack>

          <Stack direction="col" gap={2}>
            <Label className="text-slate-300 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Date of Birth</span>
              <span className="text-red-400">*</span>
            </Label>
            <DatePicker
              className="w-full"
              disabled={loading || saving}
              maxDate={new Date()}
              onChange={(value) => {
                if (value instanceof Date && !Array.isArray(value) && !("start" in value)) {
                  setBirthdate(value);
                } else if (value === null) {
                  setBirthdate(null);
                }
              }}
              placeholder="Select your birthdate"
              size="md"
              {...(birthdate === null ? {} : { value: birthdate })}
            />
            <p className="text-slate-500 text-xs">
              This information is used for age calculations and personalization features.
            </p>
          </Stack>

          <Stack className="flex-wrap" direction="row" gap={2}>
            <Button
              disabled={loading || saving || !birthdate}
              onClick={() => {
                void handleSave();
              }}
              size="md"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isConfigured ? "Update Birthdate" : "Save Birthdate"}
                </>
              )}
            </Button>

            {isEditing ? (
              <Button disabled={saving} onClick={handleCancel} size="md" variant="ghost">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    );
  };

  const renderConfiguredState = () => {
    if (!isConfigured || isEditing) {
      return null;
    }

    return (
      <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
        <Stack direction="col" gap={4}>
          <Stack align="center" direction="row" justify="between">
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Birthdate Configured
              </Heading>
            </Stack>

            <Button onClick={handleEdit} size="md" variant="ghost">
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Stack>

          <Stack direction="col" gap={2}>
            <p className="text-slate-300 text-sm">
              Your birthdate is securely stored and will be used for age calculations and
              personalization features.
            </p>
            <p className="text-slate-500 text-xs">
              This information helps provide personalized experiences throughout the application.
            </p>
          </Stack>
        </Stack>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <User className="w-5 h-5 text-slate-400" />
          Personal Information Status
        </Heading>

        <StatusIndicator
          description="Your date of birth for age calculations and personalization"
          isConfigured={isConfigured}
          label="Birthdate"
          showLock
          size="md"
        />
      </div>

      {renderConfigurationForm()}
      {renderConfiguredState()}

      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">
              Birthdate {isConfigured ? "updated" : "saved"} successfully!
            </span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">Error: {error}</span>
          </Stack>
        </Card>
      ) : null}

      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading birthdate information...</span>
          </Stack>
        </Card>
      ) : null}
    </div>
  );
};
